/* slider.js - the theme's one carousel, shared by every horizontal track.

   The track works with this file blocked: it is a real scroll container with
   scroll snap, so swipe, momentum and keyboard scrolling are the browser's.
   A section ships its controls carrying `data-slider-idle` and this file takes
   that attribute off once there is something to scroll, so a visitor without JS
   meets no dead button: base.css hides an idle row outright under `.no-js`.

   THE ROW IS LAID OUT EVEN WHILE IDLE, and that is the whole point of the
   attribute rather than the `hidden` it replaced (P9 item C). A hidden element
   has no box at all: the user agent sheet says `display: none` and this theme's
   own reset says it again with `!important` (base.css). So the row had no box
   until this file mounted, and the moment it gained one every section below it
   moved down by the row's height. Measured on the served home at 390x844, cold cache: five
   such rows on one page, CLS 0.059 against 0.000 on the live theme, where the
   same controls sit `position: absolute`. An idle row is instead invisible
   (`visibility: hidden`, so it is also unfocusable and out of the accessibility
   tree) while keeping its height, and read() below is the ONE owner of the
   state, on mount and on every resize. Nothing this file does moves a box.

   A custom element, not a shopify:section:load listener: the editor replaces a
   section's innerHTML, which alone disconnects and re-upgrades the instance.
   The active index is DERIVED from track.scrollLeft, and focus is left alone:
   `inert` on an off-screen slide would strand a keyboard user mid-track. */
(() => {
  'use strict';
  if (customElements.get('pp-slider')) return;

  const motion = matchMedia('(prefers-reduced-motion: reduce)');

  /* The pointer drag, in four numbers.

     THRESHOLD is the travel that turns a press into a drag, and it is the whole
     reason a product card stays clickable: below it nothing happens at all, no
     capture, no scroll, no click guard. Five pixels is the distance a hand
     wobbles while pressing a mouse button; the browser itself uses a comparable
     slop before it starts a text selection.

     PROJECT is how long the row keeps travelling after the pointer lets go,
     expressed as the milliseconds of the release velocity it coasts for. It is
     read as a distance, not as a duration: the actual sweep is handed to the
     browser's own smooth scroll, so the easing is the platform's.

     SETTLE_TICK and SETTLE_CAP belong to the poll that puts snapping back once
     that sweep has stopped. A poll rather than the `scrollend` event because
     the event is recent and unevenly shipped, and because the cap is what makes
     the restore unconditional: a scroll that never reports stopping still gets
     its snapping back within SETTLE_CAP. */
  const THRESHOLD = 5;
  const PROJECT = 220;
  const SETTLE_TICK = 50;
  const SETTLE_CAP = 900;
  class Slider extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-slider-track]');
      if (!track) return;
      this.teardown();
      this.track = track;
      this.stops = new Set();
      this.index = -1;
      this.loop = this.hasAttribute('data-loop');
      this.delay = parseInt(this.getAttribute('data-autoplay'), 10) || 0;
      this.bn = !!track.querySelector(':scope > [data-bn-kind]');
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      this.bar = this.querySelector('[data-slider-progress-bar]');
      this.dots = this.querySelector('[data-slider-dots]');
      /* EVERY control row, not the first one. A product row can carry two since
         the 2026-09-25 carousel lot: the arrow pair in its header
         (snippets/slider-arrows.liquid) and the progress hairline under its
         track. Both ship idle and both must wake on the same measurement. */
      this.rows = [...this.querySelectorAll('[data-slider-controls]')];
      /* No reveal here: read(), at the end of this method, is the only writer
         of the idle state, so mount and resize cannot disagree about it. The
         dots are built first and land inside a row that is still invisible. */
      this.buildDots();
      this.bind();
      this.drag();
      this.read();
      this.run();
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      clearTimeout(this.timer);
      this.timer = 0;
      /* The settle poll and the click guard are not listeners on `off`: one is
         a timer and the other arms itself after the gesture is over, so both
         would outlive a section the editor has just replaced. */
      clearInterval(this.settler);
      this.settler = 0;
      if (this.disarm) this.disarm();
      this.disarm = null;
      /* A drag interrupted by an unmount would otherwise leave its inline
         overrides on the track. When the editor swaps the section's innerHTML
         the node dies with them, but a plain disconnect and reconnect keeps the
         same node, and it would come back unable to snap. */
      if (this.track) {
        this.track.style.removeProperty('scroll-snap-type');
        this.track.style.removeProperty('scroll-behavior');
        this.track.style.removeProperty('cursor');
        this.track.style.removeProperty('user-select');
      }
      if (this.watcher) this.watcher.disconnect();
      this.watcher = null;
      if (this.off) this.off.forEach((fn) => fn());
      this.off = [];
    }

    listen(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.off.push(() => target.removeEventListener(type, fn, opts));
    }

    slides() {
      return [...this.track.children];
    }

    /* Pitch is measured from two children; a page is every slide in view. */
    pitch() {
      const kids = this.track.children;
      if (kids.length > 1) {
        const step = kids[1].offsetLeft - kids[0].offsetLeft;
        if (step > 0) return step;
      }
      return this.track.clientWidth || 1;
    }

    page() {
      const step = this.pitch();
      return step * Math.max(1, Math.round(this.track.clientWidth / step));
    }

    ease() {
      return motion.matches ? 'auto' : 'smooth';
    }

    go(dir) {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      let left = t.scrollLeft + dir * this.page();
      let wrap = false; /* a wrap JUMPS, it never sweeps back */
      if (left > max - 1) {
        wrap = this.loop && t.scrollLeft > max - 1;
        left = wrap ? 0 : max;
      } else if (left < 1) {
        wrap = this.loop && t.scrollLeft < 1;
        left = wrap ? max : 0;
      }
      t.scrollTo({ left: left, behavior: wrap ? 'instant' : this.ease() });
    }

    to(i) {
      this.track.scrollTo({ left: this.pitch() * i, behavior: this.ease() });
    }

    buildDots() {
      const box = this.dots;
      if (!box) return;
      box.replaceChildren();
      const slides = this.slides();
      if (slides.length < 2) return;
      const tpl = box.getAttribute('data-label-slide') || '{index} / {count}';
      slides.forEach((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pp-dot';
        dot.setAttribute('aria-label', tpl.replace('{index}', i + 1).replace('{count}', slides.length));
        dot.addEventListener('click', () => this.to(i));
        box.append(dot);
      });
    }

    bind() {
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.read();
        });
      };
      this.listen(this.track, 'scroll', onScroll, { passive: true });
      this.listen(window, 'resize', () => this.read(), { passive: true });
      this.listen(motion, 'change', () => {
        this.run();
        this.media();
      });
      if (this.prevBtn) this.listen(this.prevBtn, 'click', () => this.go(-1));
      if (this.nextBtn) this.listen(this.nextBtn, 'click', () => this.go(1));
      /* These two guard a decoding video, so they are armed with or without
         autoplay. Focus and hover below are armed only for a moving slider. */
      this.listen(document, 'visibilitychange', () => this.hold('tab', document.hidden));
      this.watcher = new IntersectionObserver((e) => {
        this.hold('view', !e[e.length - 1].isIntersecting);
      });
      this.watcher.observe(this);
      /* Banner loop ends advance. A pointer on the hero does not stop them:
         each banner pauses itself in its own middle zone (assets/banner.js). */
      this.listen(this.track, 'pp:banner:cycle', (e) => {
        if (this.delay && !motion.matches && !this.stops.size && e.target === this.slides()[this.index])
          e.preventDefault(), this.go(1);
      });
      if (this.bn) customElements.whenDefined('pp-banner').then(() => this.isConnected && this.run());
      /* Theme editor only: a selected block brings its slide into view and
         holds autoplay until deselected. A setting change re-renders the
         section, and the editor selects the block again with detail.load. */
      this.listen(this.track, 'shopify:block:select', (e) => {
        const i = this.slides().findIndex((s) => s.contains(e.target));
        if (i < 0) return;
        this.hold('editor', true);
        this.track.scrollTo({ left: this.pitch() * i, behavior: e.detail && e.detail.load ? 'instant' : this.ease() });
      });
      this.listen(this.track, 'shopify:block:deselect', () => this.hold('editor', false));
      /* Keyboard focus inside pauses the rotation and the banners (APG);
         focus leaving the slider resumes them, an owner choice (APG waits
         for an explicit request). There is no pause button. */
      if (!this.delay && !this.bn) return;
      this.listen(this, 'focusin', (e) => {
        let k = 1;
        try {
          k = e.target.matches(':focus-visible');
        } catch {}
        this.hold('focus', k);
      });
      this.listen(this, 'focusout', (e) => this.contains(e.relatedTarget) || this.hold('focus', false));
      /* Hover holds an image-only autoplay; banner tracks keep rotating. */
      if (!this.delay || this.bn) return;
      this.listen(this, 'pointerenter', () => this.hold('hover', true));
      this.listen(this, 'pointerleave', () => this.hold('hover', false));
    }

    /* THE POINTER DRAG.

       Touch is left out on purpose. The platform already scrolls a track with
       the thumb, with momentum and rubber banding no handler here reproduces,
       and claiming the gesture would mean cancelling that scroll to redo it
       worse. What has no platform gesture at all is the MOUSE: measured on the
       dev theme before this cycle, a pointerdown / pointermove / pointerup
       sequence across a track left scrollLeft on 0, so on a desktop a row whose
       arrows are idle could not be travelled by any means.

       Pen is handled with the mouse rather than with the thumb. On a touch
       screen a pen usually pans natively like a finger, and the moment the
       compositor takes a gesture it fires `pointercancel`, which ends the drag
       below without a release. The two can therefore not both move the track:
       whoever the platform gives the gesture to is the only one moving it.

       CAPTURE IS THE DECISION, NOT THE START. setPointerCapture retargets the
       click that follows the gesture to the capturing element, so capturing on
       pointerdown would send every click to the track and no product card could
       be opened with a mouse. It is taken at the instant the travel passes
       THRESHOLD, which is the same instant the gesture stops being a click, so
       a press that never moves is never touched by any of this.

       SNAP IS OFF FOR THE LENGTH OF THE DRAG, and inline. Assigning scrollLeft
       on a snapping container re-snaps at the end of the assignment, so a
       `mandatory` track springs back on every single move and the row does not
       follow the pointer at all. Inline rather than a class because each
       section declares its own `scroll-snap-type` on its own track class, at
       the same specificity, in a {% stylesheet %} block that is concatenated
       AFTER base.css: a class would lose that duel in silence. Snapping comes
       back once the release has settled, which is what still lands the
       slideshow on a whole slide. */
    drag() {
      const t = this.track;
      const grip = { down: false, on: false, id: -1, x: 0, y: 0, from: 0, lx: 0, lt: 0, v: 0 };

      const eat = (event) => {
        event.preventDefault();
        event.stopPropagation();
      };
      /* Armed only after a real drag, and cleared by the next pointerdown as
         well as by the click it is waiting for. A gesture that ends without
         producing a click (a pointer released off the page, a cancel) can
         therefore not leave the guard standing for somebody else's click. */
      const disarm = () => t.removeEventListener('click', eat, true);

      const stop = (cancelled) => {
        if (!grip.on) {
          grip.down = false;
          return;
        }
        grip.down = false;
        grip.on = false;
        if (t.hasPointerCapture(grip.id)) t.releasePointerCapture(grip.id);
        t.style.removeProperty('cursor');
        t.style.removeProperty('user-select');
        t.style.removeProperty('scroll-behavior');
        this.hold('drag', false);
        if (cancelled) {
          t.style.removeProperty('scroll-snap-type');
          return;
        }
        t.addEventListener('click', eat, { capture: true, once: true });
        this.disarm = disarm;
        this.coast(grip.v);
      };

      this.listen(
        t,
        'pointerdown',
        (event) => {
          if (this.disarm) this.disarm();
          this.disarm = null;
          if (event.pointerType === 'touch' || !event.isPrimary || event.button !== 0) return;
          if (t.scrollWidth - t.clientWidth < 1) return;
          grip.down = true;
          grip.on = false;
          grip.id = event.pointerId;
          grip.x = event.clientX;
          grip.y = event.clientY;
          grip.from = t.scrollLeft;
          grip.lx = event.clientX;
          grip.lt = event.timeStamp;
          grip.v = 0;
        },
        { passive: true }
      );

      this.listen(
        t,
        'pointermove',
        (event) => {
          if (!grip.down || event.pointerId !== grip.id) return;
          const dx = event.clientX - grip.x;
          const dy = event.clientY - grip.y;
          if (!grip.on) {
            if (Math.abs(dx) < THRESHOLD) {
              /* Vertical wins ties, as it does in drawer-swipe.js: a pen drawn
                 down the page is reading it, not travelling this row. */
              if (Math.abs(dy) >= THRESHOLD) grip.down = false;
              return;
            }
            if (Math.abs(dy) > Math.abs(dx)) {
              grip.down = false;
              return;
            }
            grip.on = true;
            t.setPointerCapture(event.pointerId);
            clearInterval(this.settler);
            this.settler = 0;
            t.style.scrollSnapType = 'none';
            /* Direct manipulation cannot be animated: a track left on a smooth
               `scroll-behavior` would ease toward each assignment and trail the
               pointer by a whole animation. No section declares smooth any
               more, so this is a floor rather than an override. */
            t.style.scrollBehavior = 'auto';
            t.style.cursor = 'grabbing';
            t.style.userSelect = 'none';
            this.hold('drag', true);
          }
          const span = event.timeStamp - grip.lt;
          if (span > 0) {
            const step = (event.clientX - grip.lx) / span;
            /* A weighted mean, not the last sample: one stuttering frame at the
               end of a gesture must not be the thing that decides where the row
               comes to rest. */
            grip.v = grip.v ? grip.v * 0.6 + step * 0.4 : step;
            grip.lx = event.clientX;
            grip.lt = event.timeStamp;
          }
          const max = t.scrollWidth - t.clientWidth;
          t.scrollLeft = Math.max(0, Math.min(max, grip.from - dx));
        },
        { passive: true }
      );

      /* Once captured, both of these arrive on the track whatever the pointer
         is over, which is what lets a drag end outside the row. */
      this.listen(t, 'pointerup', (event) => {
        if (event.pointerId === grip.id) stop(false);
      });
      this.listen(t, 'pointercancel', (event) => {
        if (event.pointerId === grip.id) stop(true);
      });

      /* Images and links are natively draggable, and a native drag starts at
         about three pixels, BELOW our threshold: without this the browser would
         lift a ghost of the card and stop sending pointermove before the
         gesture had a chance to become a drag. */
      this.listen(t, 'dragstart', (event) => {
        if (grip.down) event.preventDefault();
      });
    }

    /* The release. Handing the projected distance to the browser's own smooth
       scroll rather than stepping it here is what keeps the coast on the
       platform's easing, and what makes it stop at the ends without a bounce of
       our own. Under reduced motion there is no projection at all: the row ends
       exactly where the pointer left it, which is the honest reading of a
       request for less movement, since the drag itself is not motion the
       visitor did not ask for but the coast is. */
    coast(v) {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      const glide = motion.matches ? 0 : v * PROJECT;
      if (Math.abs(glide) > 1) {
        const left = Math.max(0, Math.min(max, t.scrollLeft - glide));
        t.scrollTo({ left: left, behavior: this.ease() });
      }
      this.settle();
    }

    /* Snapping goes back on only once the row has stopped, because restoring it
       mid sweep re-snaps immediately and cuts the coast short. Two identical
       samples rather than one: a smooth scroll can report the same position on
       two consecutive frames at its slowest, but not twice a tick apart. */
    settle() {
      const t = this.track;
      let last = t.scrollLeft;
      let still = 0;
      const from = Date.now();
      clearInterval(this.settler);
      this.settler = setInterval(() => {
        const now = t.scrollLeft;
        still = now === last ? still + 1 : 0;
        last = now;
        if (still < 2 && Date.now() - from < SETTLE_CAP) return;
        clearInterval(this.settler);
        this.settler = 0;
        t.style.removeProperty('scroll-snap-type');
      }, SETTLE_TICK);
    }

    hold(key, on) {
      if (on) this.stops.add(key);
      else this.stops.delete(key);
      this.run();
      this.media();
    }

    /* The one place the autoplay timer starts and stops. Reduced motion never
       rotates; a banner slide leaves on its own loop end instead. */
    run() {
      clearTimeout(this.timer);
      this.timer = 0;
      const s = this.slides()[this.index];
      const bn = s && !motion.matches && customElements.get('pp-banner') && s.matches('[data-bn-kind=timeline],[data-bn-kind=steps]');
      if (!this.delay || motion.matches || this.stops.size || bn) return;
      this.timer = setTimeout(() => this.go(1), this.delay);
    }

    read() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      this.rows.forEach((row) => row.toggleAttribute('data-slider-idle', max < 1));
      /* EDGE is the slack at each end. A product track carries 0.4rem of
         padding so a focus ring is not clipped, and scroll snap parks the
         first card on that padding: measured 2026-09-25 on the dev home, every
         row rests at scrollLeft 4, so a strict `< 1` left the back arrow
         enabled on a row that had not moved. */
      const EDGE = 8;
      if (this.prevBtn) this.flag(this.prevBtn, !this.loop && t.scrollLeft < EDGE);
      if (this.nextBtn) this.flag(this.nextBtn, !this.loop && t.scrollLeft > max - EDGE);
      if (this.bar) {
        const seen = (t.scrollLeft + t.clientWidth) / (t.scrollWidth || 1);
        this.bar.style.transform = 'scaleX(' + Math.min(1, seen) + ')';
      }
      const i = Math.round(t.scrollLeft / this.pitch());
      if (i === this.index) return;
      this.index = i;
      if (this.dots) {
        [...this.dots.children].forEach((dot, n) => {
          if (n === i) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }
      this.media();
      this.run();
    }

    flag(btn, off) {
      btn.disabled = off;
      btn.setAttribute('aria-disabled', off ? 'true' : 'false');
    }

    /* Only the on screen slide of an on screen slider plays. play() rejects
       when blocked, so the promise is swallowed rather than logged. */
    media() {
      const allowed = !motion.matches && !['view', 'tab', 'focus'].some((k) => this.stops.has(k));
      /* The next slide's deferred banner images load during this dwell. */
      const nx = allowed && this.slides()[this.index + 1];
      if (nx) nx.querySelectorAll('[data-bn-defer]').forEach((m) => (Object.assign(m, m.dataset), m.removeAttribute('data-bn-defer')));
      this.slides().forEach((slide, i) => {
        if (slide.dataset.bnKind) slide.toggleAttribute('data-bn-live', i === this.index && allowed);
        const video = slide.querySelector('video');
        if (!video) return;
        if (i === this.index && allowed) {
          const started = video.play();
          if (started) started.catch(() => {});
        } else {
          video.pause();
        }
      });
    }
  }

  customElements.define('pp-slider', Slider);
})();
